import { useLabsExperiment } from "@/features/labs/labExperiment";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';

import {
  useLifecycleEvent,
  useMarketplace,
  useMySubscriptions,
  useMyExperimentAssignments,
} from '@/lib/api/hooks';
import type { MarketplaceProduct } from '@/types/api';
import { Button, Card } from '@/design-system/primitives';
import { PageHeader } from '@/components/app-shell';
import { useReferralBannerVisible } from '@/components/app-shell/referralBannerUi';
import { cn } from '@/lib/cn';
import { track } from '@/lib/analytics';
import { useExperimentVariant } from '@/lib/experiments';
import {
  PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
  PERSONALIZED_RECOMMENDATIONS_VARIANT,
} from '@/lib/personalizedRecommendations';
import { LifecycleInterestsModal } from './components/LifecycleInterestsModal';
import { LifecycleProductModal } from './components/LifecycleProductModal';
import {
  MarketplaceFiltersDrawer,
  MarketplaceFiltersPanel,
} from './components/MarketplaceFilters';
import { MemberPricingBanner } from './components/MemberPricingBanner';
import { ProductCard } from './components/ProductCard';
import { ProtocolBundleCard } from './components/ProtocolBundleCard';
import { StackRecoveryLanding } from './components/StackRecoveryLanding';
import { SwitchFormatModal } from './components/SwitchFormatModal';
import { productFormatFamily } from './productFamily';
import {
  EMPTY_MARKETPLACE_FILTERS,
  filterAndSortMarketplaceProducts,
  getMarketplaceFilterOptions,
  marketplaceFilterCount,
  type MarketplaceFilters,
  type MarketplaceSort,
} from './marketplaceFilters';
import { subscriptionBlocksRepurchase, subscriptionPurchaseIdentity } from './subscriptionOwnership';
import { useCart } from './stores/cart';
import { cartUi } from './stores/cartUi';
import { SUPPLEMENTS_EXPERIMENT_KEY } from './supplements/api';
import { SupplementsShop } from './supplements/SupplementsShop';
import { LabShop } from '@/features/labs/LabShop';

type Tab = 'protocols' | 'products' | 'supplements' | 'labs';

const LIFECYCLE_CAMPAIGN_KEY = 'product-upsell-v1';
const LIFECYCLE_EXPERIMENT_KEY = 'lifecycle-product-upsell-v1';
const LIFECYCLE_TREATMENT_VARIANT = 'variant-a';
// Gates the Protocols/stacks tab (kill switch + purchase-rate A/B).
// Weights live on the Sanity `shop-stacks-experiment` doc; no assignment
// (doc paused/absent) reads as control = tab hidden, so a broken doc
// fails safe-off.
const STACKS_EXPERIMENT_KEY = 'shop-stacks-v1';

export function MarketplaceScreen() {
  const { enabled: labsEnabled } = useLabsExperiment();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setTab] = useState<Tab>(() => labsEnabled && searchParams.get('tab') === 'labs' ? 'labs' : 'products');
  const selectTab = (nextTab: Tab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    setSearchParams(next);
  };
  const [productSearch, setProductSearch] = useState('');
  const [productSort, setProductSort] =
    useState<MarketplaceSort>('recommended');
  const [productFilters, setProductFilters] = useState<MarketplaceFilters>(
    EMPTY_MARKETPLACE_FILTERS,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Shop-card detail view: tapping a product card (outside its Add
  // button) opens the same modal the lifecycle cross-sells use.
  const [detailProduct, setDetailProduct] = useState<MarketplaceProduct | null>(
    null,
  );
  const [dismissedRecommendation, setDismissedRecommendation] = useState<
    string | null
  >(null);
  const trackedEmailClickRef = useRef<Set<string>>(new Set());
  const trackedModalViewRef = useRef<Set<string>>(new Set());
  const lifecycleEvent = useLifecycleEvent();
  const lifecycleVariant = useExperimentVariant(LIFECYCLE_EXPERIMENT_KEY);
  const stacksVariant = useExperimentVariant(STACKS_EXPERIMENT_KEY);
  const stacksEnabled = stacksVariant === 'variant-a';
  // The shared context can begin with a stale cookie. Wait for the authenticated
  // assignment response before exposing this retail experiment or counting a visit.
  const { data: supplementAssignments } = useMyExperimentAssignments();
  const supplementsVariant = supplementAssignments?.assignments[SUPPLEMENTS_EXPERIMENT_KEY] ?? null;
  const supplementsEnabled = supplementsVariant === 'variant-a';
  const tab: Tab = (selectedTab === 'supplements' && !supplementsEnabled) || (selectedTab === 'labs' && !labsEnabled)
    ? 'products' : selectedTab;
  const supplementsExposureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!supplementsVariant || supplementsExposureRef.current === supplementsVariant) return;
    supplementsExposureRef.current = supplementsVariant;
    track('$experiment_viewed', {
      experiment_key: SUPPLEMENTS_EXPERIMENT_KEY,
      variant_key: supplementsVariant,
      source: 'shop_supplements_tab',
    });
  }, [supplementsVariant]);
  useEffect(() => {
    const requested = searchParams.get('tab');
    if (requested === 'labs' && labsEnabled) setTab('labs');
    else if (requested === 'supplements' && supplementsEnabled) setTab('supplements');
    else if (requested === 'protocols' && stacksEnabled) setTab('protocols');
    else setTab('products');
  }, [searchParams, supplementsEnabled, stacksEnabled, labsEnabled]);
  const recommendationsVariant = useExperimentVariant(
    PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
  );
  const personalizedRankingEnabled =
    recommendationsVariant === PERSONALIZED_RECOMMENDATIONS_VARIANT;

  // Honor `?view=cart` deep-links by opening the shared AppLayout cart
  // drawer once on mount, then strip the param so back/forward isn't sticky.
  useEffect(() => {
    if (searchParams.get('view') === 'cart') {
      cartUi.open();
      const next = new URLSearchParams(searchParams);
      next.delete('view');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data, isLoading, isError } = useMarketplace();
  const { data: subsData } = useMySubscriptions();
  const cartCount = useCart((s) => s.items.length);
  // Whether the fixed bottom ReferralBanner is on screen — the sticky
  // "Add to Stack" CTA repositions around it (above it when shown, into its
  // slot when hidden) so the two never overlap.
  const referralBannerVisible = useReferralBannerVisible();

  // Hide products the patient already has an active subscription for.
  // Patient sees only what they can newly purchase — duplicates remain
  // server-rejected with a friendly error if they slip through (e.g.
  // they were mid-purchase when the sub became active).
  const ownedCanonicals = new Set(
    (subsData?.subscriptions ?? [])
      .filter(subscriptionBlocksRepurchase)
      .map((s) => s.product_id)
      .filter((id): id is string => Boolean(id))
      .map(subscriptionPurchaseIdentity),
  );
  const recommendationByProductId = new Map(
    (data?.recommendations ?? []).map((recommendation) => [
      recommendation.product_id,
      recommendation,
    ]),
  );
  const unsortedVisibleProducts = (data?.products ?? []).filter(
    (p) =>
      !p.owned &&
      (!p.canonical_product_id ||
        !ownedCanonicals.has(subscriptionPurchaseIdentity(p.canonical_product_id))),
  );
  const catalogProducts = personalizedRankingEnabled
    ? [...unsortedVisibleProducts].sort((a, b) => {
        const aRank =
          recommendationByProductId.get(a.id)?.rank ?? Number.MAX_SAFE_INTEGER;
        const bRank =
          recommendationByProductId.get(b.id)?.rank ?? Number.MAX_SAFE_INTEGER;
        return aRank - bRank;
      })
    : unsortedVisibleProducts;
  const filterOptions = useMemo(
    () => getMarketplaceFilterOptions(catalogProducts),
    [catalogProducts],
  );
  const visibleProducts = useMemo(
    () =>
      filterAndSortMarketplaceProducts(
        catalogProducts,
        productSearch,
        productFilters,
        productSort,
      ),
    [catalogProducts, productFilters, productSearch, productSort],
  );
  const ownedHiddenCount =
    (data?.products?.length ?? 0) - catalogProducts.length;
  // An open one-time course is owned without being a subscription.
  const ownedHiddenCourse = (data?.products ?? []).some(
    (p) =>
      p.purchase_mode === 'one_time_course' &&
      !unsortedVisibleProducts.includes(p),
  );
  // Stacks: only protocol docs that actually declare constituents (the
  // legacy branded-protocol PDP skeletons have no bundleProducts).
  const stacks = stacksEnabled
    ? (data?.protocols ?? []).filter((pr) => pr.products.length >= 2)
    : [];

  // Exposure event for the stacks experiment — fired once per mount for
  // BOTH cohorts (control's exposure = seeing the shop without the tab)
  // so the purchase-rate comparison has a denominator.
  const stacksExposureRef = useRef(false);
  useEffect(() => {
    if (stacksExposureRef.current || stacksVariant == null) return;
    stacksExposureRef.current = true;
    track('$experiment_viewed', {
      experiment_key: STACKS_EXPERIMENT_KEY,
      variant_key: stacksVariant,
      source: 'shop_stacks_tab',
    });
  }, [stacksVariant]);
  const recommendationsExposureRef = useRef(false);
  useEffect(() => {
    if (
      recommendationsExposureRef.current ||
      recommendationsVariant == null ||
      !data
    ) {
      return;
    }
    recommendationsExposureRef.current = true;
    track('$experiment_viewed', {
      experiment_key: PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
      variant_key: recommendationsVariant,
      source: 'shop_recommendations_ranking',
      eligible: (data.recommendations?.length ?? 0) > 0,
      recommendation_count: data.recommendations?.length ?? 0,
      recommendation_model: data.recommendation_model ?? null,
      rendered_experience: personalizedRankingEnabled
        ? 'personalized_shop_order'
        : 'default_shop_order',
    });
  }, [data, personalizedRankingEnabled, recommendationsVariant]);
  // Format-switch candidates: a visible product whose chemical family
  // matches something the patient already subscribes to (NAD nasal while
  // owning NAD injection, etc.). The card offers "switch" instead of a
  // duplicate purchase; eligibility (order still pending, pricing) is
  // resolved server-side when the modal opens.
  const [switchProduct, setSwitchProduct] = useState<MarketplaceProduct | null>(
    null,
  );
  const ownedFamilies = new Set(
    [...ownedCanonicals].map((c) => productFormatFamily(c)),
  );
  const isFormatSwitchCandidate = (p: MarketplaceProduct) =>
    Boolean(
      p.canonical_product_id &&
      !p.owned &&
      !ownedCanonicals.has(p.canonical_product_id) &&
      ownedFamilies.has(productFormatFamily(p.canonical_product_id)),
    );
  const campaignParam = searchParams.get('campaign');
  const messageParam = searchParams.get('message');
  const productParam = searchParams.get('product');
  const productsParam = searchParams.get('products');
  const recommendationId = searchParams.get('rec');
  const homeDetailParam = searchParams.get('detail');
  // `products=slug,slug` — intake-interest deep links carrying several
  // treatments at once (crosssell-shop-to-all personalization).
  const interestSlugs = useMemo(
    () =>
      (productsParam ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [productsParam],
  );
  // Any lifecycle campaign deep link opens the informational modal. Only
  // the legacy product-upsell campaign is additionally gated on its
  // experiment variant (its Resend-era links could reach control patients);
  // AC-delivered campaigns only ever email treatment cohorts.
  const isLegacyUpsell = campaignParam === LIFECYCLE_CAMPAIGN_KEY;
  const lifecycleDeepLinkEnabled =
    Boolean(campaignParam) &&
    (Boolean(productParam) || interestSlugs.length > 0) &&
    (!isLegacyUpsell || lifecycleVariant === LIFECYCLE_TREATMENT_VARIANT);
  const findProduct = (needle: string) =>
    catalogProducts.find(
      (product) =>
        product.slug === needle ||
        product.id === needle ||
        product.canonical_product_id === needle,
    );
  const interestProducts = useMemo(() => {
    if (!lifecycleDeepLinkEnabled || interestSlugs.length === 0) return [];
    return interestSlugs
      .map(findProduct)
      .filter((p): p is MarketplaceProduct => Boolean(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifecycleDeepLinkEnabled, interestSlugs, catalogProducts]);
  const lifecycleProduct = useMemo(() => {
    if (!lifecycleDeepLinkEnabled) return null;
    // An interest list that resolves to a single visible product (the rest
    // owned or unpublished) falls back to the richer single-product modal.
    if (interestProducts.length >= 2) return null;
    if (interestProducts.length === 1) return interestProducts[0];
    if (!productParam) return null;
    return findProduct(productParam) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lifecycleDeepLinkEnabled,
    interestProducts,
    productParam,
    catalogProducts,
  ]);
  const recommendationKey = `${campaignParam ?? ''}:${messageParam ?? ''}:${recommendationId ?? ''}:${productParam ?? ''}:${productsParam ?? ''}`;
  const showLifecycleModal =
    Boolean(lifecycleProduct) && dismissedRecommendation !== recommendationKey;
  const showInterestsModal =
    interestProducts.length >= 2 &&
    dismissedRecommendation !== recommendationKey;

  // A treatment row on the personalized Home card lands in Shop with this
  // dedicated query param. Resolve only products the recommendation endpoint
  // actually selected, open their detail modal, then consume the param so a
  // refresh/back navigation does not keep reopening it. The Home footer omits
  // `detail`, preserving ordinary Shop navigation.
  useEffect(() => {
    if (!homeDetailParam || !data) return;
    const product = (data.products ?? []).find(
      (candidate) =>
        candidate.slug === homeDetailParam ||
        candidate.id === homeDetailParam ||
        candidate.canonical_product_id === homeDetailParam,
    );
    const recommendation = product
      ? (data.recommendations ?? []).find(
          (candidate) => candidate.product_id === product.id,
        )
      : undefined;
    if (product && recommendation) {
      setTab('products');
      setDetailProduct(product);
      track('marketplace_product_detail_opened', {
        product_id: product.id,
        slug: product.slug,
        source: 'home_recommendations_card',
        recommended: true,
        recommendation_rank: recommendation.rank,
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('detail');
    setSearchParams(next, { replace: true });
  }, [data, homeDetailParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (showLifecycleModal || showInterestsModal) setTab('products');
  }, [showLifecycleModal, showInterestsModal]);

  // Control cohort (or kill switch): the Protocols tab doesn't exist —
  // never leave the view stranded on it.
  useEffect(() => {
    if (!stacksEnabled && tab === 'protocols') setTab('products');
  }, [stacksEnabled, tab]);

  useEffect(() => {
    if (!lifecycleDeepLinkEnabled) return;
    if (trackedEmailClickRef.current.has(recommendationKey)) return;
    trackedEmailClickRef.current.add(recommendationKey);
    track('lifecycle_email_clicked', {
      campaign_key: campaignParam,
      message_key: messageParam,
      recommendation_id: recommendationId,
      product: productParam,
      products: productsParam,
    });
    lifecycleEvent.mutate({
      campaign_key: campaignParam!,
      message_key: messageParam,
      recommendation_id: recommendationId,
      event_type: 'email_clicked',
      metadata: { product: productParam, products: productsParam },
    });
  }, [
    campaignParam,
    lifecycleDeepLinkEnabled,
    lifecycleEvent,
    messageParam,
    productParam,
    productsParam,
    recommendationId,
    recommendationKey,
  ]);

  useEffect(() => {
    if (!showInterestsModal) return;
    const viewKey = `interests:${recommendationKey}`;
    if (trackedModalViewRef.current.has(viewKey)) return;
    trackedModalViewRef.current.add(viewKey);
    const productIds = interestProducts.map((p) => p.id);
    track('lifecycle_product_modal_viewed', {
      campaign_key: campaignParam,
      message_key: messageParam,
      recommendation_id: recommendationId,
      product_ids: productIds,
      source: 'lifecycle_interests',
    });
    lifecycleEvent.mutate({
      campaign_key: campaignParam!,
      message_key: messageParam,
      recommendation_id: recommendationId,
      event_type: 'modal_viewed',
      metadata: { product_ids: productIds },
    });
  }, [
    campaignParam,
    interestProducts,
    lifecycleEvent,
    messageParam,
    recommendationId,
    recommendationKey,
    showInterestsModal,
  ]);

  useEffect(() => {
    if (!showLifecycleModal || !lifecycleProduct) return;
    if (trackedModalViewRef.current.has(recommendationKey)) return;
    trackedModalViewRef.current.add(recommendationKey);
    track('$experiment_viewed', {
      experiment_key: LIFECYCLE_EXPERIMENT_KEY,
      variant_key: lifecycleVariant,
      source: 'lifecycle_product_upsell',
      campaign_key: campaignParam,
      message_key: messageParam,
      recommendation_id: recommendationId,
      product_id: lifecycleProduct.id,
    });
    track('lifecycle_product_modal_viewed', {
      campaign_key: campaignParam,
      message_key: messageParam,
      recommendation_id: recommendationId,
      product_id: lifecycleProduct.id,
      slug: lifecycleProduct.slug,
    });
    lifecycleEvent.mutate({
      campaign_key: campaignParam!,
      message_key: messageParam,
      recommendation_id: recommendationId,
      event_type: 'modal_viewed',
      metadata: {
        product_id: lifecycleProduct.id,
        slug: lifecycleProduct.slug,
      },
    });
  }, [
    campaignParam,
    lifecycleEvent,
    lifecycleProduct,
    lifecycleVariant,
    messageParam,
    recommendationId,
    recommendationKey,
    showLifecycleModal,
  ]);

  const closeLifecycleModal = (open: boolean) => {
    if (open) return;
    setDismissedRecommendation(recommendationKey);
    const next = new URLSearchParams(searchParams);
    next.delete('campaign');
    next.delete('message');
    next.delete('product');
    next.delete('products');
    next.delete('rec');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Shop"
        description={labsEnabled
          ? "Browse treatments, supplements, and at-home lab tests. Choose a category to get started."
          : supplementsEnabled
          ? "Browse compounds, protocols, and supplements for your routine. Choose a category to get started."
          : "Browse protocols and single-compound products. Add to your stack and we'll charge your saved card — your clinician writes the script once you complete the medical intake."
        }
      />

      <div className="flex flex-col w-full">
        <div
          className={cn(
            'p-2 grid',
            labsEnabled
              ? stacksEnabled && supplementsEnabled ? 'grid-cols-2 sm:grid-cols-4'
                : stacksEnabled || supplementsEnabled ? 'grid-cols-3' : 'grid-cols-2'
              : stacksEnabled && supplementsEnabled ? 'grid-cols-3'
                : stacksEnabled || supplementsEnabled ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {stacksEnabled && (
            <TabButton
              active={tab === 'protocols'}
              onClick={() => selectTab('protocols')}
              dataTour="shop-protocols-tab"
            >
              Stacks <span className="hidden sm:inline">[{stacks.length}]</span>
            </TabButton>
          )}
          <TabButton
            active={tab === 'products'}
            onClick={() => selectTab('products')}
            dataTour="shop-products-tab"
            ariaLabel={`Single compounds [${catalogProducts.length}]`}
          >
            <span className="sm:hidden">Compounds</span>
            <span className="hidden sm:inline">Single compounds [{catalogProducts.length}]</span>
          </TabButton>
          {supplementsEnabled && (
            <TabButton
              active={tab === 'supplements'}
              onClick={() => selectTab('supplements')}
              dataTour="shop-supplements-tab"
            >
              Supplements
            </TabButton>
          )}
          {labsEnabled && <TabButton active={tab === 'labs'} onClick={() => selectTab('labs')} dataTour="shop-labs-tab">Labs</TabButton>}
        </div>

        {(tab === 'products' || tab === 'protocols') && <MemberPricingBanner />}
        <StackRecoveryLanding products={data?.products} shopFailed={isError} />

        {tab === 'labs' ? (
          <LabShop />
        ) : tab === 'supplements' ? (
          <SupplementsShop />
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[32rem] bg-sunken animate-pulse" />
            ))}
          </div>
        ) : tab === 'protocols' ? (
          stacks.length === 0 ? (
            <ProtocolsComingSoon />
          ) : (
            <div
              data-tour="shop-protocols-grid"
              className="grid grid-cols-1 md:grid-cols-2 gap-20 p-15 pt-13"
            >
              {stacks.map((pr) => (
                <ProtocolBundleCard
                  key={pr.id}
                  protocol={pr}
                  products={data?.products ?? []}
                  ownedCanonicals={ownedCanonicals}
                />
              ))}
            </div>
          )
        ) : catalogProducts.length === 0 ? (
          <EmptyState tab="products" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[24rem_minmax(0,1fr)] lg:items-start gap-0 lg:gap-20 px-15 pt-13">
            <MarketplaceFiltersPanel
              filters={productFilters}
              needOptions={filterOptions.needs}
              methodOptions={filterOptions.methods}
              onChange={setProductFilters}
            />

            <section className="min-w-0 flex flex-col gap-15">
              <MarketplaceToolbar
                query={productSearch}
                sort={productSort}
                resultCount={visibleProducts.length}
                filterCount={marketplaceFilterCount(productFilters)}
                onQueryChange={setProductSearch}
                onSortChange={setProductSort}
                onOpenFilters={() => setFiltersOpen(true)}
              />

              {ownedHiddenCount > 0 && (
                <p className="type-body-sm text-ink-muted">
                  {ownedHiddenCount} product{ownedHiddenCount === 1 ? '' : 's'}{' '}
                  hidden —{' '}
                  {ownedHiddenCourse
                    ? 'already purchased or in progress.'
                    : 'already in your subscriptions.'}
                </p>
              )}

              {visibleProducts.length === 0 ? (
                <FilteredEmptyState
                  onClear={() => {
                    setProductSearch('');
                    setProductFilters(EMPTY_MARKETPLACE_FILTERS);
                  }}
                />
              ) : (
                <div
                  data-tour="shop-products-grid"
                  className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-20 sm:gap-20"
                >
                  {visibleProducts.map((p) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                      recommendation={
                        personalizedRankingEnabled
                          ? recommendationByProductId.get(p.id)
                          : undefined
                      }
                      onOpenDetail={setDetailProduct}
                      onSwitchFormat={
                        isFormatSwitchCandidate(p)
                          ? setSwitchProduct
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Sticky "Add to Stack" CTA — fixed at viewport bottom whenever
          the cart has at least one item. Tapping opens the shared
          AppLayout cart drawer (same as the SearchBar's cart icon and
          the header Cart button), where the user reviews + Confirms
          Purchase. */}
        {cartCount > 0 && tab !== 'labs' && (
          <div
            className={cn(
              // Mirror the ReferralBanner's exact horizontal footprint so the
              // CTA stays inline with — and clear of — the chat launcher
              // (right-60 leaves the gap; matching width keeps the left gutter).
              'fixed z-40 right-60 w-[calc(100%-7.5rem)] md:w-[32rem]',
              // Only the vertical offset differs. Banner sits at bottom-60
              // (mobile) / bottom-20 (desktop) and is h-40 tall: when shown,
              // float 5px above its top edge; when hidden, drop into its slot.
              referralBannerVisible
                ? 'bottom-[10.5rem] md:bottom-[6.5rem]'
                : 'bottom-60 md:bottom-20',
            )}
          >
            <Button
              variant="primary"
              className="w-full justify-between shadow-elevated bg-accent hover:bg-[var(--accent-hover)]"
              onClick={() => cartUi.open()}
            >
              <span className="blink">{tab === 'supplements' ? 'View cart' : 'Add to Stack'}</span>
              <span className="blink type-label-sm opacity-75">
                {cartCount} {cartCount === 1 ? 'item' : 'items'}
              </span>
            </Button>
          </div>
        )}
      </div>
      <MarketplaceFiltersDrawer
        open={filtersOpen}
        filters={productFilters}
        needOptions={filterOptions.needs}
        methodOptions={filterOptions.methods}
        onClose={() => setFiltersOpen(false)}
        onApply={(filters) => {
          setProductFilters(filters);
          setFiltersOpen(false);
        }}
        getResultCount={(filters) =>
          filterAndSortMarketplaceProducts(
            catalogProducts,
            productSearch,
            filters,
            productSort,
          ).length
        }
      />
      {switchProduct && (
        <SwitchFormatModal
          product={switchProduct}
          onClose={() => setSwitchProduct(null)}
        />
      )}
      <LifecycleProductModal
        open={Boolean(detailProduct)}
        onOpenChange={(o) => {
          if (!o) setDetailProduct(null);
        }}
        product={catalogProducts.find((product) => product.id === detailProduct?.id) ?? null}
        eyebrow="PRODUCT DETAILS"
        recommendation={
          detailProduct && personalizedRankingEnabled
            ? recommendationByProductId.get(detailProduct.id)
            : undefined
        }
        onSwitchFormat={
          detailProduct && isFormatSwitchCandidate(detailProduct)
            ? (p) => {
                setDetailProduct(null);
                setSwitchProduct(p);
              }
            : undefined
        }
      />
      <LifecycleProductModal
        open={showLifecycleModal}
        onOpenChange={closeLifecycleModal}
        product={lifecycleProduct}
        campaignKey={campaignParam ?? ''}
        messageKey={messageParam}
        recommendationId={recommendationId}
        onAddToCart={() => {
          if (!lifecycleProduct) return;
          lifecycleEvent.mutate({
            campaign_key: campaignParam!,
            message_key: messageParam,
            recommendation_id: recommendationId,
            event_type: 'add_to_cart',
            metadata: {
              product_id: lifecycleProduct.id,
              slug: lifecycleProduct.slug,
            },
          });
        }}
      />
      <LifecycleInterestsModal
        open={showInterestsModal}
        onOpenChange={closeLifecycleModal}
        products={interestProducts}
        campaignKey={campaignParam ?? ''}
        messageKey={messageParam}
        recommendationId={recommendationId}
        onAddToCart={(product) => {
          lifecycleEvent.mutate({
            campaign_key: campaignParam!,
            message_key: messageParam,
            recommendation_id: recommendationId,
            event_type: 'add_to_cart',
            metadata: { product_id: product.id, slug: product.slug },
          });
        }}
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
  dataTour,
  ariaLabel,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** Guided-tour spotlight anchor (features/tour/GuidedTour). */
  dataTour?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={dataTour}
      aria-label={ariaLabel}
      aria-pressed={active}
      className={cn(
        'min-h-44 min-w-0 px-5 text-12 sm:text-13 leading-120 transition-colors focus-visible:shadow-focus',
        active
          ? 'bg-[var(--accent)] text-white'
          : 'bg-[var(--accent-hover)] text-white/70',
      )}
    >
      {children}
    </button>
  );
}

function MarketplaceToolbar({
  query,
  sort,
  resultCount,
  filterCount,
  onQueryChange,
  onSortChange,
  onOpenFilters,
}: {
  query: string;
  sort: MarketplaceSort;
  resultCount: number;
  filterCount: number;
  onQueryChange: (value: string) => void;
  onSortChange: (value: MarketplaceSort) => void;
  onOpenFilters: () => void;
}) {
  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col md:flex-row gap-10">
        <label className="relative flex-1 min-w-0">
          <span className="sr-only">Search treatments</span>
          <Search
            aria-hidden
            size={18}
            strokeWidth={1.5}
            className="absolute left-15 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search treatments"
            autoComplete="off"
            className="w-full h-48 bg-raised border border-line-subtle pl-44 pr-44 font-body text-16 md:text-14 text-ink placeholder:text-ink-muted focus-visible:outline-none focus-visible:border-accent focus-visible:shadow-focus"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear treatment search"
              onClick={() => onQueryChange('')}
              className="absolute right-4 top-4 h-40 w-40 flex items-center justify-center text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:shadow-focus"
            >
              <X aria-hidden size={16} strokeWidth={1.5} />
            </button>
          )}
        </label>

        <div className="grid grid-cols-2 gap-10 md:flex md:shrink-0">
          <button
            type="button"
            onClick={onOpenFilters}
            className="lg:hidden h-48 px-15 border border-line-default bg-page flex items-center justify-center gap-8 type-label-sm hover:border-line-strong focus-visible:outline-none focus-visible:shadow-focus"
          >
            <SlidersHorizontal aria-hidden size={16} strokeWidth={1.5} />
            Filters{filterCount > 0 ? ` (${filterCount})` : ''}
          </button>

          <label className="relative min-w-0 md:w-[20rem]">
            <span className="sr-only">Sort treatments</span>
            <select
              value={sort}
              onChange={(event) =>
                onSortChange(event.target.value as MarketplaceSort)
              }
              className="appearance-none w-full h-48 bg-raised border border-line-subtle pl-15 pr-40 font-mono text-12 uppercase text-ink focus-visible:outline-none focus-visible:border-accent focus-visible:shadow-focus"
            >
              <option value="recommended">Recommended</option>
              <option value="name-asc">Name A–Z</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
            </select>
            <ChevronDown
              aria-hidden
              size={16}
              strokeWidth={1.5}
              className="absolute right-15 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </label>
        </div>
      </div>

      <p className="type-label-xs text-ink-muted tabular-nums" role="status">
        {resultCount} {resultCount === 1 ? 'treatment' : 'treatments'}
      </p>
    </div>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Card padding="lg">
      <div className="flex flex-col items-center text-center gap-10 py-30">
        <div className="type-label-md">No matching treatments</div>
        <p className="type-body-sm text-ink-secondary max-w-[48ch]">
          Try a different search or remove one of the selected filters.
        </p>
        <Button type="button" variant="secondary" onClick={onClear}>
          Clear search and filters
        </Button>
      </div>
    </Card>
  );
}

function EmptyState({ tab }: { tab: Tab }) {
  return (
    <Card padding="lg">
      <div className="flex flex-col items-center text-center gap-10 py-20">
        <div className="type-label-md text-ink-muted">
          No {tab === 'protocols' ? 'protocols' : 'products'} published yet
        </div>
        <p className="type-body-sm text-ink-secondary max-w-[48ch]">
          {tab === 'protocols'
            ? 'Protocol bundles will appear here once published in Sanity.'
            : 'Single-compound products will appear here once published in Sanity.'}
        </p>
      </div>
    </Card>
  );
}

function ProtocolsComingSoon() {
  return (
    <Card padding="lg">
      <div className="flex flex-col items-center text-center gap-10 py-40">
        <div className="type-label-md text-ink-muted">Coming soon</div>
        <p className="type-body-sm text-ink-secondary max-w-[48ch]">
          Curated multi-compound protocols are on the way. In the meantime,
          browse single compounds and we'll guide you on stacking them with your
          clinician.
        </p>
      </div>
    </Card>
  );
}
